import { useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';

/**
 * The venue field, with a Google Places typeahead in front of it.
 *
 * The framing matters, because it is what keeps this inside Google's terms.
 * The widget is a *typeahead that pre-fills planner-owned fields*, not a data
 * source we read from. The planner accepts a suggestion, the strings become
 * their data — theirs to edit, theirs to paste into vendor email as `{{venue}}`
 * — and the only Google-derived value that lives in the row afterwards is
 * `place_id`, which the terms permit storing indefinitely. Nothing here caches
 * a name, an address or a photo for later, and nothing asks for coordinates:
 * those may only be held for 30 days and no screen in this app draws a map.
 *
 * Without `VITE_GOOGLE_MAPS_API_KEY` the component is a plain text input and
 * loads nothing at all. That is deliberate rather than defensive: CI and a
 * fresh checkout have no key, and the venue field must not have a hard
 * dependency on a third party being reachable to be usable. Its placeholder is
 * "Still deciding" — free text is a first-class answer here, not a fallback.
 */

export interface VenueSelection {
  venueName: string;
  /** Set only by an accepted suggestion, and only ever alongside the other two. */
  venuePlaceId: string | null;
  venueAddress: string | null;
}

interface VenueAutocompleteProps {
  id: string;
  value: string;
  placeholder?: string;
  onChange: (next: VenueSelection) => void;
}

/**
 * The slice of the Maps JS API this file touches, hand-declared rather than
 * pulled in as `@types/google.maps`: four shapes are cheaper to state than a
 * whole ambient package is to install and keep pinned.
 */
interface GooglePlace {
  id: string;
  displayName?: string | null;
  formattedAddress?: string | null;
  fetchFields(request: { fields: string[] }): Promise<unknown>;
}

interface PlacePrediction {
  toPlace(): GooglePlace;
}

interface PlaceAutocompleteElement extends HTMLElement {
  value: string;
  placeholder: string;
}

interface PlacesLibrary {
  PlaceAutocompleteElement: new () => PlaceAutocompleteElement;
}

interface MapsWindow extends Window {
  google?: { maps: { importLibrary(name: string): Promise<unknown> } };
  [MAPS_READY]?: () => void;
}

const MAPS_READY = '__vendoropsMapsReady';

/**
 * Module-level, so the two wedding dialogs — which can both be mounted at once
 * — share one load instead of racing two script tags onto the page. The Maps
 * bootstrap refuses a second injection anyway; this way we never attempt one.
 */
let placesLibrary: Promise<PlacesLibrary> | null = null;

function loadPlacesLibrary(apiKey: string): Promise<PlacesLibrary> {
  placesLibrary ??= new Promise<void>((resolve, reject) => {
    const w = window as MapsWindow;
    w[MAPS_READY] = resolve;

    const script = document.createElement('script');
    script.async = true;
    // `loading=async` is what the current loader documentation asks for; the
    // named `callback` is how the API signals it is ready to be imported from.
    script.src = `https://maps.googleapis.com/maps/api/js?${new URLSearchParams({
      key: apiKey,
      v: 'weekly',
      libraries: 'places',
      loading: 'async',
      callback: MAPS_READY,
    })}`;
    script.onerror = () => reject(new Error('The Google Maps JavaScript API could not load.'));
    document.head.appendChild(script);
  }).then(async () => {
    const maps = (window as MapsWindow).google!.maps;
    return (await maps.importLibrary('places')) as PlacesLibrary;
  });

  return placesLibrary;
}

export default function VenueAutocomplete(props: VenueAutocompleteProps) {
  const apiKey = String(import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '');

  // No key, no widget, no script tag, no effect, no network. The branch lives
  // out here — above every hook — so the unkeyed path runs no lifecycle at all
  // rather than mounting an effect that immediately decides to do nothing.
  if (!apiKey) return <PlainVenueInput {...props} />;

  return <PlacesVenueInput {...props} apiKey={apiKey} />;
}

function PlainVenueInput({ id, value, placeholder, onChange }: VenueAutocompleteProps) {
  return (
    <Input
      id={id}
      placeholder={placeholder}
      value={value}
      onChange={(e) =>
        // Typed text is the planner's own, so it never carries a place id.
        onChange({ venueName: e.target.value, venuePlaceId: null, venueAddress: null })
      }
    />
  );
}

function PlacesVenueInput({
  id,
  value,
  placeholder,
  onChange,
  apiKey,
}: VenueAutocompleteProps & { apiKey: string }) {
  const host = useRef<HTMLDivElement>(null);

  // The element owns its own text once mounted, so `value` seeds it and is not
  // pushed back in on every render — writing to `.value` mid-typing would
  // fight the widget for the caret. Both refs keep the effect off the render
  // path: it must not re-run and re-inject the element when the parent
  // re-renders with a new closure.
  const latestOnChange = useRef(onChange);
  latestOnChange.current = onChange;
  const initialValue = useRef(value);

  useEffect(() => {
    let cancelled = false;
    let element: PlaceAutocompleteElement | null = null;

    loadPlacesLibrary(apiKey)
      .then((places) => {
        if (cancelled || !host.current) return;

        element = new places.PlaceAutocompleteElement();
        element.id = id;
        if (placeholder) element.placeholder = placeholder;
        element.value = initialValue.current;
        host.current.appendChild(element);

        // Any keystroke is the planner writing their own text, which the id
        // and address from an earlier suggestion no longer describe. All three
        // are only ever set together, by an accepted suggestion — so a manual
        // edit clears the other two together, here. Without this a planner who
        // picks "Hollow Creek Barn" and then types a different venue over it
        // saves a place_id pointing at the venue they just replaced.
        element.addEventListener('input', () => {
          latestOnChange.current({
            venueName: element!.value,
            venuePlaceId: null,
            venueAddress: null,
          });
        });

        // Fires after the element has already filled its own text and emitted
        // the `input` above, so this lands last and is authoritative.
        element.addEventListener('gmp-select', (event) => {
          const { placePrediction } = event as unknown as { placePrediction: PlacePrediction };
          const place = placePrediction.toPlace();
          // Three fields, and only three. `location` is deliberately absent.
          void place.fetchFields({ fields: ['id', 'displayName', 'formattedAddress'] }).then(() => {
            if (cancelled) return;
            latestOnChange.current({
              venueName: place.displayName ?? '',
              venuePlaceId: place.id,
              venueAddress: place.formattedAddress ?? null,
            });
          });
        });
      })
      .catch(() => {
        // A key that cannot load is a degraded typeahead, not a broken form —
        // the host stays empty and the planner keeps the field they had.
      });

    return () => {
      cancelled = true;
      element?.remove();
    };
  }, [apiKey, id, placeholder]);

  return (
    <div
      ref={host}
      // The element draws its own input, including the "Powered by Google"
      // attribution the terms require — which is why nothing here hides any of
      // its chrome. Sizing and rounding are set on the element directly, the
      // only styling hook it documents, so it reads as the same control as the
      // `<Input>` beside it rather than something bolted on.
      className="[&>gmp-place-autocomplete]:block [&>gmp-place-autocomplete]:h-10 [&>gmp-place-autocomplete]:w-full [&>gmp-place-autocomplete]:rounded-2xl [&>gmp-place-autocomplete]:text-base md:[&>gmp-place-autocomplete]:text-sm"
    />
  );
}
